import { useEffect, useState } from 'react';
import { ArrowRightLeft, Phone, PhoneOff, UserPlus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useVoiceAgentStore } from '@stores/voiceAgentStore';
import voiceService, { type VoiceSession } from '@services/voice.service';
import { parseHandoffReason } from '../utils/parseHandoffReason';
import { callClock } from '../callConsole';
import { startRingtone, unlockRingtoneOnFirstGesture } from './ringtone';
import { fieldLabel } from '../utils/fieldLabel';
import { TransferDialog } from './TransferDialog';

/** At most this many collected details on the ring; the rest are on the call screen. */
const MAX_DETAILS = 6;

/**
 * Longest the browser rings on its own. The server moves the call on after its
 * ring timeout (30s) and says so; this covers that message never arriving.
 */
const MAX_RING_MS = 45_000;

/**
 * The ring. Everything the agent needs to decide and to open the call well:
 * who is calling, why they want a person, what the AI already has from them
 * (so the agent does not ask again), and how long it has been ringing.
 *
 * Rings out loud, and raises a desktop notification when the tab is in the
 * background — an agent working in another tab otherwise never hears it.
 */
export function IncomingCallModal() {
  const { incomingCall, callContext, ringingSince, acceptCall, rejectCall } = useVoiceAgentStore();
  const navigate = useNavigate();
  const [session, setSession] = useState<VoiceSession | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [transferring, setTransferring] = useState(false);

  const sessionId = incomingCall ? callContext?.sessionId : undefined;

  // The full context from the session: summary and collected details.
  useEffect(() => {
    setSession(null);
    if (!sessionId) return;
    let cancelled = false;
    voiceService
      .getSession(sessionId)
      .then((s) => !cancelled && setSession(s))
      .catch(() => {
        // The ring still shows what the offer carried.
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  // Audio must be unlocked by a click before a ring can be heard.
  useEffect(() => {
    unlockRingtoneOnFirstGesture();
  }, []);

  // While ringing: the ring itself, the clock, the tab title, a desktop
  // notification when this window is not in front, and a ceiling on how long
  // the browser rings if it never hears that the call moved on.
  useEffect(() => {
    if (!incomingCall) return;
    const call = incomingCall;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    const stopRing = startRingtone();

    const title = document.title;
    let flip = false;
    const flash = setInterval(() => {
      flip = !flip;
      document.title = flip ? '📞 Incoming call' : title;
    }, 1000);

    let notice: Notification | undefined;
    try {
      const notLooking = document.hidden || !document.hasFocus();
      if (notLooking && 'Notification' in window && Notification.permission === 'granted') {
        notice = new Notification('Incoming call', {
          body: `${callContext?.customerPhone ?? 'A caller'} wants to speak to a person`,
          tag: 'centraone-incoming-call',
          requireInteraction: true,
        });
        notice.onclick = () => {
          window.focus();
          notice?.close();
        };
      }
    } catch {
      // Notifications unavailable: the ring still sounds and shows.
    }

    const giveUp = setTimeout(() => {
      const state = useVoiceAgentStore.getState();
      if (state.incomingCall !== call) return;
      call.reject();
      state.setIncomingCall(null);
      state.setNotice(`You missed a call from ${callContext?.customerPhone ?? 'a caller'}.`);
    }, MAX_RING_MS);

    return () => {
      clearInterval(tick);
      clearInterval(flash);
      clearTimeout(giveUp);
      document.title = title;
      stopRing();
      notice?.close();
    };
  }, [incomingCall]); // eslint-disable-line react-hooks/exhaustive-deps

  // A ring that ends (answered, declined, passed on) takes the picker with it.
  useEffect(() => {
    if (!incomingCall) setTransferring(false);
  }, [incomingCall]);

  // The agent pressed Accept on this caller in the queue: their ring is the
  // answer to that, so it is answered straight away rather than rung again.
  const answeringSessionId = useVoiceAgentStore((s) => s.answeringSessionId);
  useEffect(() => {
    if (!incomingCall || !answeringSessionId) return;
    if (callContext?.sessionId !== answeringSessionId) return;
    useVoiceAgentStore.getState().setAnsweringSessionId(null);
    acceptCall();
    navigate('/live-call/active');
  }, [incomingCall, answeringSessionId, callContext?.sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!incomingCall) return null;

  const summary = callContext?.aiSummary;
  const parsed = parseHandoffReason(callContext?.handoffReason, summary?.keyIssue);
  const summaryText: string | undefined =
    (summary?.text && summary.text !== 'No summary available' ? summary.text : undefined) ||
    session?.aiSummaryText ||
    undefined;
  const reason = session?.handoffReason || session?.handoff?.reason || parsed.briefReason;
  const details = Object.entries(session?.collectedDetails ?? {})
    .filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== '')
    .slice(0, MAX_DETAILS);
  const ringing = ringingSince ? Math.max(0, Math.floor((now - ringingSince) / 1000)) : 0;

  const handleAccept = () => {
    acceptCall();
    navigate('/live-call/active');
  };

  // Answer, then straight into "Add to call": a colleague joins once the
  // caller is connected to this agent.
  const handleAnswerAndAdd = () => {
    useVoiceAgentStore.getState().setAddColleagueOnAnswer(true);
    handleAccept();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" role="dialog" aria-modal="true" aria-label="Incoming call">
      <div
        className="w-full max-w-md overflow-hidden border border-[var(--co-line)] bg-[var(--co-raised)]"
        style={{ borderRadius: 14, boxShadow: 'var(--co-shadow-card)' }}
      >
        {/* Who, and how long it has been ringing. */}
        <div className="flex items-center gap-4 px-6 py-5" style={{ background: 'var(--co-ink)' }}>
          <div
            className="flex h-12 w-12 flex-none animate-pulse items-center justify-center rounded-[12px]"
            style={{ background: 'var(--co-brass)', color: 'var(--co-ink)' }}
          >
            <Phone className="h-6 w-6" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="co-mono text-[10px] uppercase tracking-[.14em]" style={{ color: 'var(--co-brass)' }}>
              Incoming call · ringing {callClock(ringing)}
            </p>
            <p className="co-mono mt-1 truncate text-[20px] font-bold" style={{ color: 'var(--co-on-ink)' }}>
              {voiceService.formatPhoneNumber(callContext?.customerPhone || '') || 'Unknown caller'}
            </p>
            {details.find(([k]) => k === 'customer_name') && (
              <p className="truncate text-[12.5px]" style={{ color: 'var(--co-on-ink)' }}>
                {String(details.find(([k]) => k === 'customer_name')![1])}
              </p>
            )}
          </div>
        </div>

        <div className="max-h-[50vh] space-y-4 overflow-y-auto px-6 py-5">
          {callContext?.invitedBy && (
            <div
              className="rounded-[10px] border px-3 py-2"
              style={{ borderColor: 'var(--co-brass)', background: 'var(--co-brass-wash)' }}
            >
              <p className="co-mono text-[9.5px] font-semibold uppercase tracking-[.16em] text-[var(--co-brass-ink)]">
                Join a call
              </p>
              <p className="mt-1 text-[13px] leading-[1.45] text-[var(--co-ink)]">
                {callContext.invitedBy} invites you to join their call. You will hear them and the caller.
              </p>
            </div>
          )}
          {callContext?.transferReturned && (
            <div
              className="rounded-[10px] border px-3 py-2"
              style={{ borderColor: 'var(--co-brass)', background: 'var(--co-brass-wash)' }}
            >
              <p className="co-mono text-[9.5px] font-semibold uppercase tracking-[.16em] text-[var(--co-brass-ink)]">
                Back to you
              </p>
              <p className="mt-1 text-[13px] leading-[1.45] text-[var(--co-ink)]">
                Nobody took the transfer, so the caller is still waiting for you.
              </p>
            </div>
          )}
          {callContext?.transferredBy && (
            <div
              className="rounded-[10px] border px-3 py-2"
              style={{ borderColor: 'var(--co-brass)', background: 'var(--co-brass-wash)' }}
            >
              <p className="co-mono text-[9.5px] font-semibold uppercase tracking-[.16em] text-[var(--co-brass-ink)]">
                Transferred by {callContext.transferredBy}
              </p>
              {callContext.transferNote && (
                <p className="mt-1 text-[13px] leading-[1.45] text-[var(--co-ink)]">{callContext.transferNote}</p>
              )}
            </div>
          )}
          <Section label="Wants a person because">{reason}</Section>
          {summaryText && <Section label="Summary">{summaryText}</Section>}
          {session?.ticketTypeName && <Section label="Request">{session.ticketTypeName}</Section>}
          {details.filter(([k]) => k !== 'customer_name').length > 0 && (
            <div>
              <p className="co-mono text-[9.5px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]">
                Already collected
              </p>
              <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2">
                {details
                  .filter(([k]) => k !== 'customer_name')
                  .map(([key, value]) => (
                    <div key={key} className="min-w-0">
                      <dt className="truncate text-[11px] text-[var(--co-ink-3)]">{fieldLabel(key)}</dt>
                      <dd className="truncate text-[12.5px] font-medium text-[var(--co-ink)]">{String(value)}</dd>
                    </div>
                  ))}
              </dl>
            </div>
          )}
        </div>

        <div className="flex gap-3 border-t border-[var(--co-line)] px-6 py-4">
          <button
            type="button"
            onClick={rejectCall}
            className="flex h-11 flex-1 items-center justify-center gap-2 rounded-[10px] border text-[13px] font-semibold"
            style={{ borderColor: 'var(--co-burgundy-line)', color: 'var(--co-burgundy)', background: 'var(--co-burgundy-wash)' }}
          >
            <PhoneOff className="h-4 w-4" />
            Decline
          </button>
          {/* Busy? Hand it to a colleague of your choosing instead of declining.
              Not an invitation to join someone's call: that is theirs to move. */}
          {callContext?.sessionId && !callContext.invitedBy && (
            <button
              type="button"
              onClick={() => setTransferring(true)}
              className="flex h-11 flex-1 items-center justify-center gap-2 rounded-[10px] border border-[var(--co-line-strong)] text-[13px] font-semibold text-[var(--co-ink-2)]"
            >
              <ArrowRightLeft className="h-4 w-4" />
              Transfer
            </button>
          )}
          {incomingCall.conferenceable && !callContext?.invitedBy && (
            <button
              type="button"
              onClick={handleAnswerAndAdd}
              title="Answer, then add a colleague: all three of you talk"
              className="flex h-11 flex-1 items-center justify-center gap-2 rounded-[10px] border border-[var(--co-line-strong)] text-[13px] font-semibold text-[var(--co-ink-2)]"
            >
              <UserPlus className="h-4 w-4" />
              Merge
            </button>
          )}
          <button
            type="button"
            onClick={handleAccept}
            autoFocus
            className="flex h-11 flex-1 items-center justify-center gap-2 rounded-[10px] text-[13px] font-bold text-white"
            style={{ background: 'var(--co-green)' }}
          >
            <Phone className="h-4 w-4" />
            Accept
          </button>
        </div>
      </div>
      {transferring && <TransferDialog onClose={() => setTransferring(false)} />}
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="co-mono text-[9.5px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]">{label}</p>
      <p className="mt-1 text-[13px] leading-[1.45] text-[var(--co-ink)]">{children}</p>
    </div>
  );
}
